import { Link, Stack, Text } from "@fluentui/react";
import { Box, Card } from "@mui/material";
import React, { useMemo, useState } from "react";
import { PromptLibraryItem } from "../../../../interfaces/slices.interfaces";

interface PromptPackSectionProps {
    prompts: PromptLibraryItem[];
    scenarioGrouping: string;
    onRunPrompt?: (prompt: PromptLibraryItem, action: "run" | "open") => void;
}

/**
 * Parses the prompt text to extract title and description.
 * Title is the text before the first ":", description is everything after.
 * @param prompt - The full prompt text
 * @returns Object containing parsed title and description
 */
const parsePromptText = (prompt: string): { parsedTitle: string; parsedDescription: string } => {
    if (!prompt) {
        return { parsedTitle: "", parsedDescription: "" };
    }

    const colonIndex = prompt.indexOf(":");
    if (colonIndex === -1) {
        return { parsedTitle: prompt, parsedDescription: "" };
    }

    return {
        parsedTitle: prompt.substring(0, colonIndex).trim(),
        parsedDescription: prompt.substring(colonIndex + 1).trim()
    };
};

/**
 * Gets unique categories from prompts, filtering out empty ones
 */
const getUniqueCategories = (prompts: PromptLibraryItem[]): string[] => {
    const categories = new Set<string>();
    prompts.forEach((prompt) => {
        if (prompt.promptCategory && prompt.promptCategory.trim()) {
            categories.add(prompt.promptCategory.trim());
        }
    });
    return Array.from(categories).sort();
};

/**
 * PromptPackCard - A card component that displays a prompt with parsed title and description
 * Matches the mock design with blue left border, title, description, and "Copy prompt" link
 */
const PromptPackCard: React.FC<PromptLibraryItem & { onRunPrompt?: PromptLibraryItem["onRunPrompt"] }> = (prompt) => {
    const { parsedTitle, parsedDescription } = useMemo(
        () => parsePromptText(prompt.description || prompt.prompt),
        [prompt.description, prompt.prompt]
    );

    // Use the parsed title if available, otherwise use original title
    const displayTitle = parsedTitle || prompt.title;
    const displayDescription = parsedDescription || prompt.description;

    // Copy the full prompt to clipboard
    const handleCopyPrompt = () => {
        const textToCopy = prompt.description || prompt.prompt || "";
        navigator.clipboard.writeText(textToCopy);
    };

    return (
        <Card
            sx={{
                borderLeft: "4px solid #005597",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
                minHeight: 180,
                padding: "16px 20px",
                backgroundColor: "#f0f8ff",
                boxShadow: "none",
                borderRadius: "4px",
                transition: "box-shadow 0.2s ease-in-out",
                "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,85,151,0.12)"
                }
            }}
        >
            <Stack tokens={{ childrenGap: 12 }}>
                <Text
                    variant="mediumPlus"
                    styles={{
                        root: {
                            color: "#005597",
                            fontWeight: 600,
                            lineHeight: 1.4
                        }
                    }}
                >
                    {displayTitle}
                </Text>
                <Text
                    variant="medium"
                    styles={{
                        root: {
                            color: "#323130",
                            lineHeight: 1.5,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: "vertical"
                        }
                    }}
                >
                    {displayDescription}
                </Text>
            </Stack>
            <Stack horizontal horizontalAlign="end" styles={{ root: { marginTop: 16 } }}>
                <Link
                    onClick={handleCopyPrompt}
                    styles={{
                        root: {
                            color: "#005597",
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            textDecoration: "none",
                            "&:hover": {
                                textDecoration: "underline"
                            }
                        }
                    }}
                >
                    <span style={{ fontSize: 14 }}>↗</span> Copy prompt
                </Link>
            </Stack>
        </Card>
    );
};

/**
 * CategoryTab - A pill-style tab button for category filtering
 */
const CategoryTab: React.FC<{
    label: string;
    isSelected: boolean;
    onClick: () => void;
}> = ({ label, isSelected, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            backgroundColor: isSelected ? "#005597" : "white",
            border: "2px solid #005597",
            borderRadius: "20px",
            color: isSelected ? "white" : "#005597",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 500,
            padding: "8px 20px",
            transition: "all 0.2s ease",
            whiteSpace: "nowrap",
            "&:hover": {
                backgroundColor: isSelected ? "#004480" : "rgba(0, 85, 151, 0.08)"
            }
        }}
    >
        {label}
    </Box>
);

/**
 * PromptPackSection - Displays prompts grouped by scenario with category filter tabs
 */
export const PromptPackSection: React.FC<PromptPackSectionProps> = ({ prompts, scenarioGrouping, onRunPrompt }) => {
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    // Get unique categories
    const categories = useMemo(() => getUniqueCategories(prompts), [prompts]);

    // Filter prompts by selected category
    const filteredPrompts = useMemo(() => {
        if (!selectedCategory) {
            return prompts;
        }
        return prompts.filter(
            (prompt) => prompt.promptCategory?.trim() === selectedCategory ||
                (selectedCategory === "None of these" && (!prompt.promptCategory || !prompt.promptCategory.trim()))
        );
    }, [prompts, selectedCategory]);

    // Check if there are prompts without a category
    const hasUncategorizedPrompts = useMemo(
        () => prompts.some((p) => !p.promptCategory || !p.promptCategory.trim()),
        [prompts]
    );

    // Don't render if no prompts
    if (!prompts || prompts.length === 0) {
        return null;
    }

    return (
        <Box sx={{ marginBottom: 4, marginTop: 2 }}>
            {/* Scenario Group Header */}
            <Box
                sx={{
                    background: "linear-gradient(135deg, #005597 0%, #0078d4 100%)",
                    borderRadius: "8px 8px 0 0",
                    color: "white",
                    padding: "16px 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Text variant="xLarge" styles={{ root: { color: "white", fontWeight: 600 } }}>
                    Prompt Pack ({scenarioGrouping || "General"})
                </Text>
            </Box>

            {/* Category Filter Tabs - Pill style buttons */}
            {categories.length > 0 && (
                <Box
                    sx={{
                        backgroundColor: "white",
                        borderLeft: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        padding: "16px 24px",
                        alignItems: "center"
                    }}
                >
                    {categories.map((category) => (
                        <CategoryTab
                            key={category}
                            label={category}
                            isSelected={selectedCategory === category}
                            onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                        />
                    ))}
                    {hasUncategorizedPrompts && (
                        <CategoryTab
                            label="None of these"
                            isSelected={selectedCategory === "None of these"}
                            onClick={() => setSelectedCategory(selectedCategory === "None of these" ? null : "None of these")}
                        />
                    )}
                </Box>
            )}

            {/* Prompt Cards Grid - 2 columns like mock */}
            <Box
                sx={{
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "0 0 8px 8px",
                    borderTop: categories.length > 0 ? "none" : "1px solid #e0e0e0",
                    padding: "24px"
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gap: 3,
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, 1fr)"
                        }
                    }}
                >
                    {filteredPrompts.map((prompt) => (
                        <PromptPackCard key={prompt.id} {...prompt} onRunPrompt={onRunPrompt} />
                    ))}
                </Box>
                {filteredPrompts.length === 0 && (
                    <Text variant="medium" styles={{ root: { color: "#666", fontStyle: "italic", textAlign: "center" } }}>
                        No prompts found in this category.
                    </Text>
                )}
            </Box>
        </Box>
    );
};

export default PromptPackSection;
